import {
  FilterNone,
  IndeterminateCheckBox,
  KeyboardArrowDown,
  LibraryAddCheck,
  Search
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Input,
  InputAdornment,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { reducerUpdateAdvanceSearch } from 'src/features/advanceSearchEcommerce';
import { ILabel } from 'src/models/general';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { useUnlimitedScroll } from 'src/hooks/useUnlimitedScroll';
import InfiniteScroll from 'react-infinite-scroll-component';
import Empty from 'src/components/Empty';

export default function FilterLabel() {
  const theme = useTheme();
  const { selectedLabel } = useAppSelector(
    (state) => state.storeAdvancedSearchEcommerce
  );
  const labelEcommerce = useAppSelector(
    (state) => state.storeLabel.labelEcommerce
  );
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const dispatch = useAppDispatch();

  const advanceSearchEcommerce = useAppSelector(
    (state) => state.storeAdvancedSearchEcommerce
  );

  const [selectedLabelLocal, setSelectedLabelLocal] = useState<ILabel[]>([]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      setSelectedLabelLocal(labelEcommerce);
    } else {
      setSelectedLabelLocal([]);
    }
  };

  const handleLabelCheckChange = (label: ILabel, checked: boolean) => {
    if (checked) {
      setSelectedLabelLocal([...selectedLabelLocal, label]);
    } else {
      setSelectedLabelLocal(
        selectedLabelLocal.filter((item) => item.id !== label.id)
      );
    }
  };

  const handleApply = () => {
    dispatch(
      reducerUpdateAdvanceSearch({
        ...advanceSearchEcommerce,
        labelList: selectedLabelLocal.map((item) => item.id),
        selectedLabel: selectedLabelLocal
      })
    );
    handleClose();
  };

  const addName = useMemo((): Array<ILabel> => {
    return labelEcommerce.map((label) => ({
      ...label,
      name: label.name
    }));
  }, [labelEcommerce]);

  const { filteredList: unsortedFilteredList, handleFilter } =
    useFiltersByName<ILabel>(addName);

  const filteredList = useMemo(() => {
    if (!selectedLabelLocal.length) return unsortedFilteredList;
    if (!unsortedFilteredList.length) return [];

    const activeIdsSet = new Set(selectedLabelLocal.map((item) => item.id));
    return unsortedFilteredList.sort((a, b) => {
      const aIsActive = activeIdsSet.has(a.id);
      const bIsActive = activeIdsSet.has(b.id);
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;
      return 0;
    });
  }, [unsortedFilteredList, selectedLabelLocal]);

  const { fetchMoreData, infiniteScroll } =
    useUnlimitedScroll<ILabel>(filteredList);

  useEffect(() => {
    setSelectedLabelLocal(selectedLabel);
  }, [selectedLabel]);

  return (
    <>
      <Stack maxWidth="400px" width="100%" spacing={0.5}>
        <Typography variant="h5" fontWeight={600}>
          Label
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={handleOpen}
          sx={{
            color: 'text.primary',
            backgroundColor: 'white',
            borderRadius: theme.spacing(1),
            maxWidth: '400px',
            width: '100%',
            minHeight: '36px',
            borderColor: (theme) =>
              open ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.23)',
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.87)',
              backgroundColor: 'white'
            },
            '& .MuiButton-startIcon': { marginRight: 'auto' },
            '& .buttonText': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              textAlign: 'left',
              flex: '1'
            },
            padding: '4px 8px'
          }}
          endIcon={
            <KeyboardArrowDown
              sx={{
                transform: open ? 'rotate(180deg)' : 'rotate(0)',
                marginRight: '2px'
              }}
            />
          }
        >
          {selectedLabel.length <= 0 ? (
            <span className="buttonText">All Label</span>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 0.5,
                // maxWidth: '320px',
                // maxWidth: '500px',
                width: '100%',
                overflow: 'scroll',
                scrollbarWidth: 'none'
              }}
            >
              {selectedLabel.map((label, index) => (
                <Chip
                  key={`${label}-${index}`}
                  variant="outlined"
                  sx={{
                    maxWidth: selectedLabel.length > 1 ? '150px' : '300px',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block'
                    }
                  }}
                  label={label.name}
                  size="small"
                />
              ))}
            </Box>
          )}
        </Button>
      </Stack>
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Box sx={{ p: theme.spacing(2), maxWidth: '600px', width: '100%' }}>
          <Typography variant="h4">Label</Typography>
          <Box
            sx={{ mt: theme.spacing(1), maxHeight: '300px', overflow: 'auto' }}
          >
            <>
              <Input
                startAdornment={
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                }
                fullWidth
                placeholder="Search Label"
                onChange={(event) => handleFilter(event.target.value)}
              />
              <TableContainer
                component={Paper}
                sx={{
                  mt: theme.spacing(2),
                  maxHeight: 480
                }}
              >
                <Table stickyHeader>
                  <TableHead
                    sx={{
                      background: theme.palette.primary.main
                    }}
                  >
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ width: '3%' }}>
                        <Checkbox
                          checked={
                            labelEcommerce.length === selectedLabelLocal.length
                          }
                          indeterminate={
                            selectedLabelLocal.length > 0 &&
                            labelEcommerce.length !== selectedLabelLocal.length
                          }
                          size="small"
                          onChange={handleSelectAllChange}
                          icon={
                            <FilterNone
                              sx={{
                                color: theme.palette.common.white,
                                width: theme.spacing(2.5),
                                height: theme.spacing(2.5)
                              }}
                            />
                          }
                          checkedIcon={
                            <LibraryAddCheck
                              sx={{
                                color: theme.palette.common.white,
                                width: theme.spacing(2.5),
                                height: theme.spacing(2.5)
                              }}
                            />
                          }
                          indeterminateIcon={
                            <IndeterminateCheckBox
                              sx={{
                                color: theme.palette.common.white,
                                width: theme.spacing(2.5),
                                height: theme.spacing(2.5)
                              }}
                            />
                          }
                        />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {labelEcommerce.length < 0 ? (
                      <TableRow hover={false}>
                        <TableCell align="center" colSpan={4}>
                          <Box
                            sx={{
                              height: 300,
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}
                          >
                            <Empty message="There's no data yet" />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <InfiniteScroll
                        dataLength={infiniteScroll.list.length}
                        next={fetchMoreData}
                        hasMore={infiniteScroll.hasMore}
                        loader={<div />}
                        height={250}
                        endMessage={<div />}
                      >
                        {infiniteScroll.list.map((row) => (
                          <TableRow
                            key={row.id}
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0
                              }
                            }}
                          >
                            <TableCell padding="checkbox" sx={{ width: '3%' }}>
                              <Stack direction="row" gap={1}>
                                <Checkbox
                                  color="primary"
                                  checked={selectedLabelLocal.some(
                                    (label) => label.id === row.id
                                  )}
                                  onChange={(event) =>
                                    handleLabelCheckChange(
                                      row,
                                      event.target.checked
                                    )
                                  }
                                />
                              </Stack>
                            </TableCell>
                            <TableCell
                              padding="none"
                              sx={{ flex: 1 }}
                              colSpan={2}
                            >
                              {row.name}
                            </TableCell>
                          </TableRow>
                        ))}
                      </InfiniteScroll>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          </Box>
          <Stack pt={2} alignItems="end">
            <Button variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
